import Vue from "vue";
import * as _ from "lodash";
import { createUser, deleteUser, getUsers, updateMe, updateUser, updateUserPassword, USER_API } from "../../api/user";
import { getDarkMode, setDarkMode } from "../../utils/auth";
import { log2 } from "../../utils/log2";
import { publishMessage } from "../../utils/systemPubSub";
import { sendSocketMessage } from "../../utils/socketStream";

if (getDarkMode()) document.body.classList.add("dark-mode");
else document.body.classList.remove("dark-mode");

const log = log2("user.js");
const user = {
    namespaced: true,

    state: {
        users: {
            total: 0,
            count: 0,
            data: [],
            initialized: false,
        },
        activeUserCount: 0,
        darkMode: getDarkMode(),
    },
    getters: {
        getUser: (state) => (id) => {
            return state.users.data.find((u) => u._id === id);
        },
        isDarkMode: (state, rootState) => state.darkMode,
    },
    mutations: {
        SET_USERS: (state, payload) => {
            log("SET_USERS", payload);
            const oldList = state.users.data;
            let newList = payload.data;
            newList = newList.map((x) => {
                const oldObj = _.find(oldList, (q) => q._id === x._id);
                if (oldObj) {
                    const oldKeys = _.keys(oldObj);
                    const newKeys = _.keys(x);
                    const diffKeys = _.difference(oldKeys, newKeys);
                    diffKeys.forEach((key) => {
                        x[key] = oldObj[key];
                    });
                    return x;
                } else {
                    return x;
                }
            });
            payload.data = newList;
            payload.initialized = true;
            log("users state updated", newList);
            state.users = payload;
        },
        ADD_USER: (state, payload) => {
            state.users.data.push(payload);
            state.users.total++;
        },
        UPDATE_USER: (state, payload) => {
            let user = state.users.data.findIndex((p) => p._id === payload._id);
            state.users.data[user] = payload;
        },
        UPDATE_AGENT: (state, data) => {
            let { rootState, payload } = data;
            const findIndex = state.users.data.findIndex((u) => u._id === payload.userId);
            const user = state.users.data[findIndex];
            const cloneUser = _.cloneDeep(user);
            const clonePayload = _.cloneDeep(payload);
            if (user && Object.keys(payload).length > 1) {
                payload = {
                    ...payload,
                    lastStatusChangeDate: Date.now(),
                };
                try {
                    if (rootState.auth.user._id === user._id) {
                        delete payload.isOnline;
                        delete payload.status;
                    }
                } catch (e) {
                    log.error("UPDATE_AGENT error", e);
                }
            }
            log("UPDATE_AGENT", payload);
            const cloneUpdatedUser = { ...cloneUser, ...clonePayload };
            const isChanged = !_.isEqual(cloneUser, cloneUpdatedUser);
            if (isChanged) {
                Vue.set(state.users.data, findIndex, { ...user, ...payload });
                publishMessage("agent-updated");
                publishMessage("user_update", data);
            } else {
                log("UPDATE_AGENT is not changed", { userId: payload.userId });
            }
        },
        REMOVE_USER: (state, id) => {
            const userIndex = state.users.data.findIndex((u) => u._id === id);
            log("remove user starting", {
                users: state.users.data,
                index: userIndex,
            });
            if (userIndex >= 0) {
                Vue.delete(state.users.data, userIndex);
                state.users.total--;
            }
            log("remove user ending", {
                users: state.users.data,
                index: userIndex,
            });
        },
        SET_DARK_MODE: (state, payload) => {
            setDarkMode(payload);
            state.darkMode = payload;
            if (payload) document.body.classList.add("dark-mode");
            else document.body.classList.remove("dark-mode");
        },
        SET_ACTIVE_USER_COUNT: (state, count) => {
            state.activeUserCount = count;
        },
    },
    actions: {
        getUsers({ commit, dispatch }, payload) {
            log("getUsers", payload);
            const updateStore = typeof payload.updateStore === "boolean" ? payload.updateStore : true;
            dispatch("getActiveUserCount");
            return getUsers(payload.perPage, payload.page, payload.query, payload.sortBy, payload.sortOrder).then(
                (response) => {
                    response.data = {
                        ...response.data,
                        query: payload.query,
                    };
                    if (updateStore) commit("SET_USERS", response.data);
                    return Promise.resolve(response); //bunu return etmessek Promise<undefined> okur çağıranlar. .then() leri map gibi düşünelim container'ın taşıdığı tipi kaybetmeyelim. Aynı durum diğer yerlerde de var
                }
            );
        },
        getUser({ state }, id) {
            return state.users.data.find((u) => u._id === id);
        },
        createUser({ commit }, payload) {
            return createUser(payload).then((response) => {
                commit("ADD_USER", response.data);
            });
        },
        updateUser({ commit }, payload) {
            if (payload.user) {
                return updateUser(payload.user._id, payload.user).then((response) => {
                    sendSocketMessage("team list-chats", {
                        siteId: payload.siteId,
                        userId: payload.user._id,
                        user: response.data,
                    });
                    sendSocketMessage("email update-user_departments", {
                        siteId: payload.siteId,
                        userId: payload.user._id,
                    });
                    commit("UPDATE_USER", response.data);
                });
            } else {
                return updateUser(payload._id, payload).then((response) => {
                    commit("UPDATE_USER", response.data);
                });
            }
        },
        updateMe({ commit }, payload) {
            return updateMe(payload).then((response) => {
                sendSocketMessage("team list-chats", {
                    siteId: payload.siteId,
                    userId: response.data._id,
                });
                sendSocketMessage("email update-user_departments", {
                    siteId: payload.siteId,
                });
                commit("UPDATE_USER", response.data);
            });
        },
        updateUserPassword({ commit }, payload) {
            return updateUserPassword(payload).then((response) => {});
        },
        deleteUser({ commit }, id) {
            commit("REMOVE_USER", id);
            return deleteUser(id);
        },
        userDeleted({ commit }, id) {
            commit("REMOVE_USER", id);
        },
        updateAgent({ commit, rootState }, payload) {
            commit("UPDATE_AGENT", { rootState, payload });
        },
        updateDarkMode({ commit }, payload) {
            log("updateDarkMode", payload);
            return commit("SET_DARK_MODE", payload);
        },
        getDarkMode({ state }) {
            return state.darkMode;
        },
        getActiveUserCount({ commit }) {
            return USER_API.fetchActiveUserCount().then((activeAgentCount) => {
                commit("SET_ACTIVE_USER_COUNT", activeAgentCount);
                return activeAgentCount;
            });
        },
    },
};

export default user;
